import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, OperatorFunction, delay, map, of, raceWith, switchMap, take, timer } from 'rxjs';

import { isViewTransitionsEnabled } from './view-transition-config.helper';
import { ViewTransitionStateService } from './view-transition-state.service';

/**
 * Usually called at the beginning of a navigation (in guards) and the transition pseudo-elements
 * aren't created until after NavigationEnd so the timeout has to be quite long to account for this.
 */
const DEFAULT_DEFER_TIMEOUT = 1000;

/**
 * @param force - Whether to force the deferral, regardless of navigation state or device type.
 * @param async - Whether to evaluate navigation state sync or async; use async when the source
 * may emit before the resultant navigation begins but within the same call stack.
 * @param timeout - The time limit allowed to wait for the transition.
 */
export type DeferUntilTransitionReadyOptions = {
    force?: boolean;
    async?: boolean;
    timeout?: number;
};

/** On iOS (and sometimes otherwise), it's necessary to delay UI changes until the view transition snapshot is presented */
@Injectable({
    providedIn: 'root',
})
export class ViewTransitionUtils {
    readonly #router = inject(Router);
    readonly #ngZone = inject(NgZone);
    readonly #viewTransitionStateService = inject(ViewTransitionStateService);

    #deferTimeout = DEFAULT_DEFER_TIMEOUT;

    setDeferTimeout(timeout: number): void {
        this.#deferTimeout = timeout;
    }

    deferCallbackUntilTransitionReady(callback: VoidFunction, options: DeferUntilTransitionReadyOptions = {}): void {
        of(null)
            .pipe(this.deferUntilTransitionReady(options))
            .subscribe(() => callback());
    }

    deferUntilTransitionReady<T>(options: DeferUntilTransitionReadyOptions = {}): OperatorFunction<T, T> {
        const { force = false, async = false, timeout } = options;

        return (source$: Observable<T>) => {
            if (!isViewTransitionsEnabled()) {
                return source$;
            }

            return this.#ngZone.runOutsideAngular(() =>
                source$.pipe(
                    async ? delay(0) : (source) => source,
                    switchMap((value) => this.defer(value, timeout ?? this.#deferTimeout, force)),
                ),
            );
        };
    }

    private defer<T>(value: T, timeout: number, force: boolean): Observable<T> {
        if (!this.isInNavigation() && !force) {
            return of(value);
        }

        return this.#viewTransitionStateService.whenReady.pipe(
            raceWith(timer(timeout)),
            map(() => value),
            take(1),
        );
    }

    private isInNavigation(): boolean {
        return Boolean(this.#router.getCurrentNavigation()) || this.#viewTransitionStateService.isScheduled;
    }
}
